import React, { Component } from 'react';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Button, Typography, TextField, Stepper, Step, StepLabel,
  Chip, Radio, RadioGroup, FormControlLabel, Switch, Divider, Alert, CircularProgress, IconButton, Skeleton, Tooltip,
} from '@mui/material';
import Check from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ScheduleSendOutlinedIcon from '@mui/icons-material/ScheduleSendOutlined';
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined';
import DateRangeOutlinedIcon from '@mui/icons-material/DateRangeOutlined';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import AddIcon from '@mui/icons-material/Add';
import TableChartOutlinedIcon from '@mui/icons-material/TableChartOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CustomizedSnackbars from '../material-snackbars';
import SimpleAutoComplete from '../auto-complete/autocomplete';
import SimpleAutoCompleteMultiple from '../auto-complete/autocomplete-multiple';
import RecurrenceEditor, { DEFAULT_RECURRENCE, buildScheduleSummary } from './recurrence-editor';
import RunDetailsDialog from './run-details-dialog';
import DetailsDialog, { buildFilterRows, buildMemberRows, buildMembersFooter } from './details-dialog';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import ReportScheduleService, {
  getCatalogEntry, parseRequestUrl, buildFilterSummary, RELATIVE_PERIODS, resolveRelativePeriod, inferRelativePeriod,
} from '../../services/report-schedule.service';

// Ícone de step (mesmo visual do wizard do Marketing).
function StepCircle(props) {
  const { active, completed, icon } = props;
  const base = {
    width: 30, height: 30, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 13, fontWeight: 700, transition: 'all .25s ease',
  };
  if (completed) return <Box sx={{ ...base, backgroundColor: '#5b9b1e', color: '#fff' }}><Check sx={{ fontSize: 16 }} /></Box>;
  if (active) return <Box sx={{ ...base, backgroundColor: '#8BC34A', color: '#fff', transform: 'scale(1.15)', boxShadow: '0 0 0 5px rgba(139,195,74,0.25)' }}>{icon}</Box>;
  return <Box sx={{ ...base, backgroundColor: '#efefef', color: '#a0a0a0' }}>{icon}</Box>;
}

const FORMAT_META = {
  xlsx: { icon: <TableChartOutlinedIcon />, labelId: 'report.schedule.format.xlsx', descId: 'report.schedule.format.xlsx.desc' },
  csv: { icon: <DescriptionOutlinedIcon />, labelId: 'report.schedule.format.csv', descId: 'report.schedule.format.csv.desc' },
  pdf: { icon: <PictureAsPdfOutlinedIcon />, labelId: 'report.schedule.format.pdf', descId: 'report.schedule.format.pdf.desc' },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const sectionTitle = { fontSize: 14, fontWeight: 700, color: '#333', mb: 1 };
const switchSx = { '& .Mui-checked': { color: '#8BC34A' }, '& .Mui-checked + .MuiSwitch-track': { backgroundColor: '#8BC34A !important' } };
const greenBtn = { textTransform: 'none', backgroundColor: '#8BC34A', '&:hover': { backgroundColor: '#7cb342' } };

// Assistente "Agendar envio" (criar/editar). Criação: recebe reportKey + request (URL do paginate
// que a tela acabou de executar). Edição: recebe schedule (normalizado).
class ScheduleReportDialog extends Component {
  constructor(props) {
    super(props);
    this.state = this.buildInitialState(props);
  }

  componentDidMount() {
    this.loadSources();
  }

  buildInitialState(props) {
    const { schedule, reportKey, request } = props;
    if (schedule) {
      return {
        activeStep: 0, saving: false, testing: false, loadingSources: true,
        sources: { users: [], teams: [], roles: [], marketingLists: [] }, allowedDomains: [],
        emailInput: '', emailError: '', notification: { open: false, variant: 'success', message: '' },
        nameTouched: true,
        form: { ...schedule, recurrence: schedule.recurrence || { ...DEFAULT_RECURRENCE } },
      };
    }
    const entry = getCatalogEntry(reportKey);
    const { path, params } = parseRequestUrl(request);
    const relativePeriod = inferRelativePeriod(params, entry);
    return {
      nameTouched: false,
      activeStep: 0, saving: false, testing: false, loadingSources: true,
      sources: { users: [], teams: [], roles: [], marketingLists: [] }, allowedDomains: [],
      emailInput: '', emailError: '', notification: { open: false, variant: 'success', message: '' },
      form: {
        id: null,
        name: '',
        reportKey, requestPath: path, params,
        periodMode: 'relative', relativePeriod,
        format: entry && entry.formats.length ? entry.formats[0] : 'xlsx',
        scheduleMode: 'recurring', onceAt: '', recurrence: { ...DEFAULT_RECURRENCE }, timezone: 'America/Sao_Paulo',
        deliveryMode: 'auto', linkExpirationDays: 7, skipIfEmpty: true, includeOwner: true, recipients: [],
      },
    };
  }

  loadSources() {
    Promise.all([ReportScheduleService.getRecipientSources(), ReportScheduleService.getAllowedDomains()])
      .then(([src, dom]) => {
        this.setState({
          loadingSources: false,
          sources: (src.data && src.data.success) ? src.data.data : this.state.sources,
          allowedDomains: (dom.data && dom.data.success) ? dom.data.data : [],
        });
      })
      .catch(() => this.setState({ loadingSources: false }));
  }

  get entry() { return getCatalogEntry(this.state.form.reportKey); }
  get hasDateParams() {
    const e = this.entry;
    return !!(e && e.dateParams && this.state.form.params[e.dateParams.start] !== undefined);
  }

  notify = (message, variant = 'success') => this.setState({ notification: { open: true, variant, message } });
  patchForm = (p) => this.setState(prev => ({ form: { ...prev.form, ...p } }));

  // Nome sugerido a partir do relatório + período + frequência atuais (mesma ideia do "Sugerir nome" do Marketing).
  buildSuggestedName(form) {
    const { intl } = this.props;
    const e = this.entry;
    const report = e ? intl.formatMessage({ id: e.labelId }) : form.reportKey;
    const period = !this.hasDateParams ? null
      : form.periodMode === 'fixed' ? intl.formatMessage({ id: 'report.schedule.period.fixed.short' })
        : intl.formatMessage({ id: `report.schedule.period.${form.relativePeriod}` });
    const when = buildScheduleSummary(intl, form);
    return [report, period, when].filter(Boolean).join(' · ').substring(0, 120);
  }

  suggestName = () => this.patchForm({ name: this.buildSuggestedName(this.state.form) });
  changeName = (name) => this.setState(prev => ({ nameTouched: true, form: { ...prev.form, name } }));

  // ----- modais de detalhe -----
  closeDetails = () => this.setState({ details: null });
  // Só aplica se o modal ainda estiver aberto (usuário pode fechar antes da resposta chegar).
  patchDetails = (p) => this.setState(prev => (prev.details ? { details: { ...prev.details, ...p } } : null));

  openFiltersDetails = () => {
    const { intl } = this.props;
    const { form } = this.state;
    this.setState({ details: { title: intl.formatMessage({ id: 'report.schedule.filters.title' }), subtitle: form.name, loading: true, rows: [] } });
    ReportScheduleService.describeFilters(form.reportKey, form.params)
      .then(r => this.patchDetails({ loading: false, rows: buildFilterRows(intl, (r.data && r.data.data) || []), emptyText: intl.formatMessage({ id: 'report.schedule.filters.none' }) }))
      .catch(() => this.setState({ details: null }));
  };

  openMembersDetails = (recipient) => {
    const { intl } = this.props;
    this.setState({ details: { title: recipient.name, subtitle: intl.formatMessage({ id: `report.schedule.recipientType.${recipient.type}` }), loading: true, rows: [], searchable: true } });
    ReportScheduleService.getRecipientMembers(recipient.type, recipient.id)
      .then(r => {
        const members = (r.data && r.data.data) || [];
        this.patchDetails({ loading: false, rows: buildMemberRows(intl, members), footer: buildMembersFooter(intl, members) });
      })
      .catch(err => {
        this.setState({ details: null });
        this.notify(err?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'report.schedule.toast.error' }), 'error');
      });
  };

  // Visão consolidada de TODOS os destinatários (clique em grupo abre os membros).
  openRecipientsDetails = () => {
    const { intl, currentUser } = this.props;
    const { form } = this.state;
    const rows = [];
    if (form.includeOwner) rows.push({ key: 'owner', primary: currentUser.name || '—', secondary: currentUser.email, chips: [intl.formatMessage({ id: 'report.schedule.recipientType.owner' })] });
    form.recipients.forEach((r, i) => {
      const dynamic = ['team', 'role', 'marketing_list'].includes(r.type);
      rows.push({
        key: `${r.type}-${r.id || r.name}-${i}`, primary: r.name,
        secondary: dynamic ? intl.formatMessage({ id: 'report.schedule.details.clickMembers' }) : null,
        chips: [intl.formatMessage({ id: `report.schedule.recipientType.${r.type}` })],
        onClick: dynamic ? () => this.openMembersDetails(r) : undefined,
      });
    });
    this.setState({ details: { title: intl.formatMessage({ id: 'report.schedule.review.recipients' }), subtitle: form.name, loading: false, rows } });
  };

  // ----- destinatários -----
  // Devolve as PRÓPRIAS opções (mesma referência) — o Autocomplete do MUI compara value x options por ===.
  recipientsOf = (type, options) => {
    const ids = this.state.form.recipients.filter(r => r.type === type).map(r => r.id);
    return (options || []).filter(o => ids.includes(o.id));
  };
  setRecipientsOf = (type, list) => this.setState(prev => ({
    form: {
      ...prev.form,
      recipients: [...prev.form.recipients.filter(r => r.type !== type), ...(list || []).map(i => ({ type, id: i.id, name: i.name, channel: 'email' }))],
    },
  }));

  addExternalEmail = () => {
    const { intl } = this.props;
    const email = this.state.emailInput.trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_REGEX.test(email)) { this.setState({ emailError: intl.formatMessage({ id: 'report.schedule.recipients.email.invalid' }) }); return; }
    if (!ReportScheduleService.isEmailAllowed(email)) { this.setState({ emailError: intl.formatMessage({ id: 'report.schedule.recipients.email.domainBlocked' }, { domain: email.split('@')[1] }) }); return; }
    if (this.state.form.recipients.some(r => r.type === 'email' && r.name === email)) { this.setState({ emailInput: '', emailError: '' }); return; }
    this.setState(prev => ({
      emailInput: '', emailError: '',
      form: { ...prev.form, recipients: [...prev.form.recipients, { type: 'email', id: null, name: email, channel: 'email' }] },
    }));
  };

  removeExternalEmail = (email) => this.setState(prev => ({
    form: { ...prev.form, recipients: prev.form.recipients.filter(r => !(r.type === 'email' && r.name === email)) },
  }));

  get recipientsCount() {
    const f = this.state.form;
    return f.recipients.length + (f.includeOwner ? 1 : 0);
  }

  // ----- navegação -----
  get steps() {
    const { intl } = this.props;
    return ['what', 'when', 'who', 'format'].map(s => intl.formatMessage({ id: `report.schedule.step.${s}` }));
  }

  canNext = () => {
    const { activeStep, form } = this.state;
    if (activeStep === 0) return true; // nome é validado no último passo
    if (activeStep === 1) {
      if (form.scheduleMode === 'once') return !!form.onceAt && moment(form.onceAt).isAfter(moment());
      if (form.recurrence.freq === 'weekly') return (form.recurrence.weekdays || []).length > 0;
      return !!form.recurrence.time;
    }
    if (activeStep === 2) return this.recipientsCount > 0;
    return !!form.format && form.name.trim().length > 0;
  };

  goTo = (step) => this.setState(p => (step === 3 && !p.nameTouched
    ? { activeStep: step, form: { ...p.form, name: this.buildSuggestedName(p.form) } }
    : { activeStep: step }));
  next = () => this.goTo(Math.min(3, this.state.activeStep + 1));
  back = () => this.goTo(Math.max(0, this.state.activeStep - 1));
  goToStep = (i) => { if (this.props.schedule || i < this.state.activeStep) this.goTo(i); };

  buildPayload() {
    const f = this.state.form;
    return {
      id: f.id, name: f.name.trim(), reportKey: f.reportKey, requestPath: f.requestPath, params: f.params,
      periodMode: f.periodMode, relativePeriod: f.relativePeriod, format: f.format,
      scheduleMode: f.scheduleMode, onceAt: f.onceAt, recurrence: f.recurrence, timezone: f.timezone,
      deliveryMode: f.deliveryMode, linkExpirationDays: f.linkExpirationDays, skipIfEmpty: f.skipIfEmpty,
      includeOwner: f.includeOwner, recipients: f.recipients,
    };
  }

  save = () => {
    const { intl, currentUser, onSaved } = this.props;
    this.setState({ saving: true });
    ReportScheduleService.save(this.buildPayload(), currentUser)
      .then(result => {
        this.setState({ saving: false });
        if (result.data && result.data.success) onSaved(result.data.data);
        else this.notify(intl.formatMessage({ id: 'report.schedule.toast.error' }), 'error');
      })
      .catch(err => {
        this.setState({ saving: false });
        this.notify(err?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'report.schedule.toast.error' }), 'error');
      });
  };

  sendTest = () => {
    const { intl, currentUser } = this.props;
    this.setState({ testing: true });
    ReportScheduleService.sendTest(this.buildPayload())
      .then((result) => {
        const runId = result && result.data && result.data.data ? (result.data.data.runid ?? result.data.data.runId) : null;
        // Abre o detalhe do envio de teste na hora (acompanha ao vivo até o e-mail sair ou falhar).
        this.setState({ testing: false, testRunId: runId || null });
        this.notify(intl.formatMessage({ id: 'report.schedule.toast.testSent' }, { email: currentUser.email || '' }));
      })
      .catch(err => {
        this.setState({ testing: false });
        this.notify(err?.response?.data?.errors?.[0] || intl.formatMessage({ id: 'report.schedule.toast.error' }), 'error');
      });
  };

  // ----- passo 1: O quê -----
  renderPeriodPreview() {
    const { intl } = this.props;
    const { form } = this.state;
    const e = this.entry;
    let start; let end;
    if (form.periodMode === 'fixed') {
      start = moment(form.params[e.dateParams.start]);
      end = moment(form.params[e.dateParams.end]);
    } else {
      [start, end] = resolveRelativePeriod(form.relativePeriod);
    }
    if (!start || !start.isValid() || !end || !end.isValid()) return null;
    return (
      <Typography sx={{ fontSize: 12, color: '#777', mt: 1 }}>
        {intl.formatMessage({ id: form.periodMode === 'fixed' ? 'report.schedule.period.preview.fixed' : 'report.schedule.period.preview.relative' },
          { start: start.format('DD/MM/YYYY'), end: end.format('DD/MM/YYYY') })}
      </Typography>
    );
  }

  renderStepWhat() {
    const { intl } = this.props;
    const { form } = this.state;
    const e = this.entry;
    const summary = buildFilterSummary(form.params, e);
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Chip size="small" label={e ? intl.formatMessage({ id: e.labelId }) : form.reportKey} sx={{ backgroundColor: '#eef7e3', color: '#5b9b1e', fontWeight: 700 }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Typography sx={{ ...sectionTitle, mb: 0, flex: 1, display: 'flex', alignItems: 'center', gap: 0.7 }}>
            <FilterAltOutlinedIcon sx={{ fontSize: 18, color: '#8BC34A' }} />{intl.formatMessage({ id: 'report.schedule.filters.title' })}
          </Typography>
          <Button size="small" startIcon={<VisibilityOutlinedIcon />} onClick={this.openFiltersDetails} sx={{ textTransform: 'none', color: '#277afe' }}>
            {intl.formatMessage({ id: 'report.schedule.filters.viewAll' })}
          </Button>
        </Box>
        {summary.length === 0 ? (
          <Typography sx={{ fontSize: 13, color: '#888' }}>{intl.formatMessage({ id: 'report.schedule.filters.none' })}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
            {summary.map(s => {
              const label = s.labelId ? intl.formatMessage({ id: s.labelId }) : s.label;
              const value = s.count !== undefined ? intl.formatMessage({ id: 'report.schedule.filters.selected' }, { n: s.count })
                : s.valueId ? intl.formatMessage({ id: s.valueId }) : s.value;
              return <Chip key={s.param} size="small" variant="outlined" clickable onClick={this.openFiltersDetails}
                label={`${label}: ${value}`} sx={{ borderColor: '#d6d6d6' }} />;
            })}
          </Box>
        )}

        {this.hasDateParams && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={{ ...sectionTitle, display: 'flex', alignItems: 'center', gap: 0.7 }}>
              <DateRangeOutlinedIcon sx={{ fontSize: 18, color: '#8BC34A' }} />{intl.formatMessage({ id: 'report.schedule.period.title' })}
            </Typography>
            <RadioGroup value={form.periodMode} onChange={ev => this.patchForm({ periodMode: ev.target.value })}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <FormControlLabel value="relative" control={<Radio size="small" />} label={intl.formatMessage({ id: 'report.schedule.period.relative' })} />
                <TextField select size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ minWidth: 220 }}
                  disabled={form.periodMode !== 'relative'} value={form.relativePeriod} onChange={ev => this.patchForm({ relativePeriod: ev.target.value })}>
                  {RELATIVE_PERIODS.map(p => <option key={p} value={p}>{intl.formatMessage({ id: `report.schedule.period.${p}` })}</option>)}
                </TextField>
              </Box>
              <FormControlLabel value="fixed" control={<Radio size="small" />} label={intl.formatMessage({ id: 'report.schedule.period.fixed' })} />
            </RadioGroup>
            {this.renderPeriodPreview()}
          </Box>
        )}

        <Alert severity="info" icon={<ShieldOutlinedIcon />} sx={{ mt: 3 }}>
          {intl.formatMessage({ id: 'report.schedule.scope.notice' })}
        </Alert>
      </Box>
    );
  }

  // ----- passo 3: Para quem -----
  renderStepWho() {
    const { intl } = this.props;
    const { form, sources, loadingSources, allowedDomains, emailInput, emailError } = this.state;
    const externalEmails = form.recipients.filter(r => r.type === 'email');
    const mkList = this.recipientsOf('marketing_list', sources.marketingLists)[0] || { id: 0, name: '' };
    const groups = form.recipients.filter(r => ['team', 'role', 'marketing_list'].includes(r.type));

    if (loadingSources) {
      return <Box>{[1, 2, 3, 4].map(i => <Skeleton key={i} variant="rounded" height={44} animation="wave" sx={{ mb: 1.5 }} />)}</Box>;
    }

    return (
      <Box>
        <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.channel.title' })}</Typography>
        <Box sx={{ display: 'flex', gap: 1, mb: 2.5, flexWrap: 'wrap' }}>
          <Chip icon={<EmailOutlinedIcon />} label={intl.formatMessage({ id: 'report.schedule.channel.email' })} sx={{ backgroundColor: '#8BC34A', color: '#fff', '& .MuiChip-icon': { color: '#fff' } }} />
          <Tooltip title={intl.formatMessage({ id: 'report.schedule.soon.tooltip' })}>
            <span><Chip icon={<WhatsAppIcon />} disabled label={`${intl.formatMessage({ id: 'report.schedule.channel.whatsapp' })} · ${intl.formatMessage({ id: 'report.schedule.soon' })}`} /></span>
          </Tooltip>
        </Box>

        <FormControlLabel sx={{ display: 'flex', m: 0, mb: 2, justifyContent: 'space-between' }} labelPlacement="start"
          control={<Switch checked={form.includeOwner} onChange={() => this.patchForm({ includeOwner: !form.includeOwner })} sx={switchSx} />}
          label={<Typography sx={{ fontSize: 14 }}>{intl.formatMessage({ id: 'report.schedule.recipients.includeOwner' })}</Typography>} />

        <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.recipients.internal' })}</Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 1 }}>
          <SimpleAutoCompleteMultiple label={intl.formatMessage({ id: 'report.schedule.recipients.users' })} options={sources.users}
            stateName="users" selected={this.recipientsOf('user', sources.users)} changeSelect={(n, v) => this.setRecipientsOf('user', v)} />
          <SimpleAutoCompleteMultiple label={intl.formatMessage({ id: 'report.schedule.recipients.teams' })} options={sources.teams}
            stateName="teams" selected={this.recipientsOf('team', sources.teams)} changeSelect={(n, v) => this.setRecipientsOf('team', v)} />
          <SimpleAutoCompleteMultiple label={intl.formatMessage({ id: 'report.schedule.recipients.roles' })} options={sources.roles}
            stateName="roles" selected={this.recipientsOf('role', sources.roles)} changeSelect={(n, v) => this.setRecipientsOf('role', v)} />
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mb: 2.5 }}>{intl.formatMessage({ id: 'report.schedule.recipients.dynamicHint' })}</Typography>

        <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.recipients.external' })}</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
          <TextField fullWidth size="small" InputLabelProps={{ shrink: true }} placeholder="nome@empresa.com.br"
            label={intl.formatMessage({ id: 'report.schedule.recipients.email' })} value={emailInput} error={!!emailError}
            helperText={emailError || intl.formatMessage({ id: 'report.schedule.recipients.allowedDomains' }, { domains: allowedDomains.join(', ') || '—' })}
            onChange={ev => this.setState({ emailInput: ev.target.value, emailError: '' })}
            onKeyDown={ev => { if (ev.key === 'Enter') { ev.preventDefault(); this.addExternalEmail(); } }} />
          <Button variant="outlined" startIcon={<AddIcon />} onClick={this.addExternalEmail} sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444', height: 40, whiteSpace: 'nowrap' }}>
            {intl.formatMessage({ id: 'report.schedule.recipients.add' })}
          </Button>
        </Box>
        {externalEmails.length > 0 && (
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8, mt: 1 }}>
            {externalEmails.map(r => <Chip key={r.name} size="small" label={r.name} onDelete={() => this.removeExternalEmail(r.name)} />)}
          </Box>
        )}

        <Typography sx={{ ...sectionTitle, mt: 3 }}>{intl.formatMessage({ id: 'report.schedule.recipients.marketingList' })}</Typography>
        <Box sx={{ maxWidth: 420 }}>
          <SimpleAutoComplete label={intl.formatMessage({ id: 'report.schedule.recipients.marketingList.pick' })}
            options={sources.marketingLists} stateName="marketingList" selected={mkList}
            changeSelect={(n, v) => this.setRecipientsOf('marketing_list', v && v.id ? [v] : [])} />
        </Box>
        {mkList.id ? (
          <Alert severity="warning" icon={<WarningAmberIcon />} sx={{ mt: 1.5 }}>{intl.formatMessage({ id: 'report.schedule.recipients.marketingList.warning' })}</Alert>
        ) : null}

        {groups.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.recipients.groups' })}</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
              {groups.map(g => (
                <Chip key={`${g.type}-${g.id}`} size="small" clickable icon={<GroupsOutlinedIcon />} onClick={() => this.openMembersDetails(g)}
                  label={`${intl.formatMessage({ id: `report.schedule.recipientType.${g.type}` })}: ${g.name}`}
                  sx={{ backgroundColor: '#e8f1ff', color: '#277afe', '& .MuiChip-icon': { color: '#277afe' } }} />
              ))}
            </Box>
            <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{intl.formatMessage({ id: 'report.schedule.details.clickMembers' })}</Typography>
          </Box>
        )}

        <Box onClick={this.openRecipientsDetails} sx={{
          mt: 3, p: 1.2, px: 1.5, borderRadius: '8px', backgroundColor: '#eef7e3', border: '1px solid #cfe8bf',
          display: 'flex', alignItems: 'center', cursor: 'pointer', '&:hover': { borderColor: '#8BC34A' },
        }}>
          <Typography sx={{ fontSize: 13, color: '#5b9b1e', fontWeight: 600, flex: 1 }}>
            {intl.formatMessage({ id: 'report.schedule.recipients.total' }, { n: this.recipientsCount })}
          </Typography>
          <Typography sx={{ fontSize: 12, color: '#5b9b1e', display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <VisibilityOutlinedIcon sx={{ fontSize: 16 }} />{intl.formatMessage({ id: 'report.schedule.details.view' })}
          </Typography>
        </Box>
      </Box>
    );
  }

  // ----- passo 4: Formato e opções + revisão -----
  formatCard(fmt) {
    const { intl } = this.props;
    const meta = FORMAT_META[fmt];
    const active = this.state.form.format === fmt;
    return (
      <Box key={fmt} onClick={() => this.patchForm({ format: fmt })} sx={{
        flex: 1, minWidth: 140, cursor: 'pointer', p: 1.5, borderRadius: '10px',
        border: active ? '2px solid #8BC34A' : '1px solid #e0e0e0', backgroundColor: active ? '#f3fae9' : '#fff', transition: 'all .2s',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {React.cloneElement(meta.icon, { sx: { color: active ? '#5b9b1e' : '#9e9e9e', fontSize: 20 } })}
          <Typography sx={{ fontSize: 14, fontWeight: 700, color: active ? '#5b9b1e' : '#444' }}>{intl.formatMessage({ id: meta.labelId })}</Typography>
        </Box>
        <Typography sx={{ fontSize: 12, color: '#888', mt: 0.5 }}>{intl.formatMessage({ id: meta.descId })}</Typography>
      </Box>
    );
  }

  reviewRow(labelId, value, onClick) {
    const { intl } = this.props;
    return (
      <Box onClick={onClick} sx={{
        display: 'flex', alignItems: 'center', gap: 2, py: 0.6, borderBottom: '1px dashed #eee',
        cursor: onClick ? 'pointer' : 'default', '&:hover': onClick ? { backgroundColor: '#f3f3f3' } : undefined,
      }}>
        <Typography sx={{ fontSize: 13, color: '#888', minWidth: 140 }}>{intl.formatMessage({ id: labelId })}</Typography>
        <Typography sx={{ fontSize: 13, color: '#333', fontWeight: 600, flex: 1 }}>{value}</Typography>
        {onClick && <VisibilityOutlinedIcon sx={{ fontSize: 16, color: '#277afe' }} />}
      </Box>
    );
  }

  renderStepFormat() {
    const { intl } = this.props;
    const { form } = this.state;
    const e = this.entry;
    const formats = e ? e.formats : ['xlsx'];
    const period = !this.hasDateParams ? '—'
      : form.periodMode === 'fixed' ? intl.formatMessage({ id: 'report.schedule.period.fixed' })
        : intl.formatMessage({ id: `report.schedule.period.${form.relativePeriod}` });
    return (
      <Box>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 3 }}>
          <TextField fullWidth size="small" InputLabelProps={{ shrink: true }} inputProps={{ maxLength: 120 }} required
            error={!form.name.trim()} label={intl.formatMessage({ id: 'report.schedule.name' })} value={form.name}
            onChange={ev => this.changeName(ev.target.value)} />
          <Button variant="outlined" startIcon={<AutoAwesomeOutlinedIcon />} onClick={this.suggestName}
            sx={{ textTransform: 'none', whiteSpace: 'nowrap', borderColor: '#cfcfcf', color: '#444', flexShrink: 0, height: 40 }}>
            {intl.formatMessage({ id: 'marketing.create.suggestName' })}
          </Button>
        </Box>

        <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.format.title' })}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 3 }}>{formats.map(f => this.formatCard(f))}</Box>

        <Typography sx={sectionTitle}>{intl.formatMessage({ id: 'report.schedule.delivery.title' })}</Typography>
        <RadioGroup value={form.deliveryMode} onChange={ev => this.patchForm({ deliveryMode: ev.target.value })}>
          <FormControlLabel value="auto" control={<Radio size="small" />} label={intl.formatMessage({ id: 'report.schedule.delivery.auto' })} />
          <FormControlLabel value="link" control={<Radio size="small" />} label={intl.formatMessage({ id: 'report.schedule.delivery.link' })} />
        </RadioGroup>
        <TextField select size="small" SelectProps={{ native: true }} InputLabelProps={{ shrink: true }} sx={{ mt: 1, minWidth: 220 }}
          label={intl.formatMessage({ id: 'report.schedule.delivery.linkExpiration' })} value={form.linkExpirationDays}
          onChange={ev => this.patchForm({ linkExpirationDays: Number(ev.target.value) })}>
          {[1, 3, 7].map(d => <option key={d} value={d}>{intl.formatMessage({ id: 'report.schedule.delivery.days' }, { n: d })}</option>)}
        </TextField>

        <Divider sx={{ my: 2 }} />
        <FormControlLabel sx={{ display: 'flex', m: 0, justifyContent: 'space-between' }} labelPlacement="start"
          control={<Switch checked={form.skipIfEmpty} onChange={() => this.patchForm({ skipIfEmpty: !form.skipIfEmpty })} sx={switchSx} />}
          label={<Typography sx={{ fontSize: 14 }}>{intl.formatMessage({ id: 'report.schedule.options.skipIfEmpty' })}</Typography>} />
        <FormControlLabel sx={{ display: 'flex', m: 0, mt: 1, justifyContent: 'space-between' }} labelPlacement="start"
          control={<Switch checked={false} disabled />}
          label={(
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AutoAwesomeOutlinedIcon sx={{ fontSize: 18, color: '#b0b0b0' }} />
              <Typography sx={{ fontSize: 14, color: '#999' }}>{intl.formatMessage({ id: 'report.schedule.options.aiSummary' })}</Typography>
              <Chip size="small" label={intl.formatMessage({ id: 'report.schedule.soon' })} sx={{ height: 20, fontSize: 11 }} />
            </Box>
          )} />

        <Box sx={{ mt: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: '12px', backgroundColor: '#fafafa' }}>
          <Typography sx={{ ...sectionTitle, mb: 1 }}>{intl.formatMessage({ id: 'report.schedule.review.title' })}</Typography>
          {this.reviewRow('report.schedule.review.report', e ? intl.formatMessage({ id: e.labelId }) : form.reportKey)}
          {this.reviewRow('report.schedule.review.filters', intl.formatMessage({ id: 'report.schedule.filters.count' }, { n: buildFilterSummary(form.params, e).length }), this.openFiltersDetails)}
          {this.reviewRow('report.schedule.review.period', period)}
          {this.reviewRow('report.schedule.review.when', buildScheduleSummary(intl, form))}
          {this.reviewRow('report.schedule.review.recipients', intl.formatMessage({ id: 'report.schedule.recipients.total' }, { n: this.recipientsCount }), this.openRecipientsDetails)}
          {this.reviewRow('report.schedule.review.format', intl.formatMessage({ id: FORMAT_META[form.format].labelId }))}
        </Box>
      </Box>
    );
  }

  renderStepContent() {
    switch (this.state.activeStep) {
      case 0: return this.renderStepWhat();
      case 1: return <RecurrenceEditor value={this.state.form} onChange={this.patchForm} />;
      case 2: return this.renderStepWho();
      default: return this.renderStepFormat();
    }
  }

  render() {
    const { intl, open, onClose, schedule } = this.props;
    const { activeStep, saving, testing, notification } = this.state;
    const isLast = activeStep === 3;
    const isEdit = !!schedule;

    return (
      <Dialog open={open} onClose={saving ? undefined : onClose} maxWidth="md" fullWidth PaperProps={{ sx: { maxHeight: '90vh', borderRadius: 3 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ScheduleSendOutlinedIcon sx={{ color: '#8BC34A' }} />
            <Typography sx={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
              {intl.formatMessage({ id: isEdit ? 'report.schedule.dialog.editTitle' : 'report.schedule.dialog.title' })}
            </Typography>
            <IconButton size="small" onClick={onClose} disabled={saving}><CloseIcon fontSize="small" /></IconButton>
          </Box>
          <Stepper activeStep={activeStep} alternativeLabel sx={{
            mt: 2,
            '& .MuiStepConnector-line': { borderColor: '#e6e6e6' },
            '& .MuiStepConnector-root.Mui-active .MuiStepConnector-line, & .MuiStepConnector-root.Mui-completed .MuiStepConnector-line': { borderColor: '#8BC34A' },
            '& .MuiStepLabel-label': { color: '#9e9e9e', fontSize: 13, mt: 0.5 },
            '& .MuiStepLabel-label.Mui-active': { color: '#5b9b1e', fontWeight: 700 },
            '& .MuiStepLabel-label.Mui-completed': { color: '#5b9b1e', fontWeight: 600 },
          }}>
            {this.steps.map((s, i) => (
              <Step key={s}>
                <StepLabel StepIconComponent={StepCircle} onClick={() => this.goToStep(i)}
                  sx={(isEdit || i < activeStep) ? { cursor: 'pointer', '&, & *': { cursor: 'pointer !important' } } : undefined}>{s}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </DialogTitle>

        <DialogContent dividers sx={{ p: 0 }}>
          <div style={{ maxHeight: '60vh', overflowY: 'auto', padding: '16px 20px' }}>
            {this.renderStepContent()}
          </div>
        </DialogContent>

        <DialogActions sx={{ px: 2.5, py: 1.5, justifyContent: 'space-between' }}>
          <Button onClick={activeStep === 0 ? onClose : this.back} disabled={saving} sx={{ textTransform: 'none' }}>
            {intl.formatMessage({ id: activeStep === 0 ? 'cancel' : 'marketing.back' })}
          </Button>
          {!isLast ? (
            <Button variant="contained" onClick={this.next} disabled={!this.canNext()} sx={greenBtn}>
              {intl.formatMessage({ id: 'marketing.next' })}
            </Button>
          ) : (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button variant="outlined" onClick={this.sendTest} disabled={saving || testing}
                startIcon={testing ? <CircularProgress size={16} color="inherit" /> : <SendOutlinedIcon />}
                sx={{ textTransform: 'none', borderColor: '#cfcfcf', color: '#444' }}>
                {intl.formatMessage({ id: 'report.schedule.test' })}
              </Button>
              <Button variant="contained" onClick={this.save} disabled={saving || !this.canNext()}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null} sx={greenBtn}>
                {intl.formatMessage({ id: isEdit ? 'report.schedule.saveChanges' : 'report.schedule.save' })}
              </Button>
            </Box>
          )}
        </DialogActions>

        {this.state.details && (
          <DetailsDialog key={`${this.state.details.title}-${this.state.details.subtitle}`} open onClose={this.closeDetails} {...this.state.details} />
        )}

        {this.state.testRunId && (
          <RunDetailsDialog key={this.state.testRunId} open runId={this.state.testRunId} onClose={() => this.setState({ testRunId: null })} />
        )}

        <CustomizedSnackbars variant={notification.variant} message={notification.message} isOpen={notification.open}
          toClose={() => this.setState({ notification: { ...notification, open: false } })} />
      </Dialog>
    );
  }
}

export default injectIntl(ScheduleReportDialog);
